<template>
  <div class="travel-container">
    <!-- 航迹题头 -->
    <header class="trv-header">
      <div class="trv-route" aria-hidden="true">
        <span class="trv-dot"></span><i></i><span class="trv-plane">✈</span><i></i><span class="trv-dot"></span>
      </div>
      <h1 class="trv-title">行迹 · 旅途</h1>
      <p class="trv-sub">
        循迹山海 · 拾景为记 —— jmdvirus
        <router-link to="/travelmng" class="trv-link">旅途管理</router-link>
      </p>
    </header>

    <!-- 检索 · 筛选 -->
    <div class="trv-filter">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索标题 / 副标题 / 作者 / 内容"
        prefix-icon="el-icon-search"
        clearable
        @input="onSearch"
        class="trv-search"
      />
      <el-select
        v-model="selectedCategory"
        placeholder="按标题筛选"
        @change="onCategoryChange"
        clearable
        class="trv-select"
      >
        <el-option label="全部行程" value=""></el-option>
        <el-option v-for="c in categories" :key="c" :label="c" :value="c"></el-option>
      </el-select>
      <div class="trv-sort">
        <button
          v-for="opt in sortOptions"
          :key="opt.value"
          :class="{ 'is-active': sortOrder === opt.value }"
          @click="sortOrder = opt.value"
        >{{ opt.label }}</button>
      </div>
      <span class="trv-stats">共 {{ total }} 段旅程</span>
    </div>

    <!-- 旅程 · 按年份分组 -->
    <div class="trv-body" v-if="displayItems.length">
      <section class="trv-year" v-for="group in timelineGroups" :key="group.year">
        <div class="trv-year-head">
          <span class="trv-year-num">{{ group.year }}</span>
          <i class="trv-year-rule"></i>
          <span class="trv-year-count">{{ group.items.length }} 段</span>
        </div>
        <div class="trv-cards">
          <article
            class="trv-card"
            v-for="(item, idx) in group.items"
            :key="item.title + '-' + item.createTime + '-' + idx"
            :style="{ animationDelay: (Math.min(idx, 8) * 60) + 'ms' }"
            @click="openDetail(item)"
          >
            <div class="trv-cover">
              <img v-if="coverOf(item)" :src="mediaUrl(coverOf(item))" alt="" loading="lazy" />
              <div v-else class="trv-cover-empty"><span>{{ (item.title || '旅')[0] }}</span></div>
              <span class="trv-date">{{ formatDay(item) }}</span>
              <span class="trv-cover-veil"></span>
            </div>
            <div class="trv-card-body">
              <h3 class="trv-card-title">{{ item.title }}</h3>
              <p class="trv-card-sub" v-if="item.subtitle">{{ item.subtitle }}</p>
              <p class="trv-card-excerpt">{{ excerpt(item.content, 68) }}</p>
              <div class="trv-card-meta">
                <span v-if="trackCount(item)" class="trv-meta-item"><i class="el-icon-map-location"></i>{{ trackCount(item) }} 点</span>
                <span v-if="imageCount(item)" class="trv-meta-item"><i class="el-icon-picture-outline"></i>{{ imageCount(item) }} 张</span>
                <span v-if="videoCount(item)" class="trv-meta-item"><i class="el-icon-video-camera"></i>{{ videoCount(item) }} 段</span>
                <span class="trv-open">展开行程<i class="el-icon-arrow-right"></i></span>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <!-- 空态 -->
    <div class="trv-empty" v-else-if="!isLoading">
      <span class="trv-empty-badge">未至</span>
      <p>尚无此段旅程</p>
    </div>

    <!-- 加载更多 -->
    <div class="trv-more" v-if="hasMore">
      <button class="trv-more-btn" :class="{ 'is-loading': isLoading }" @click="queryTravel">
        {{ isLoading ? '正在溯源…' : '继续前行' }}
      </button>
    </div>

    <!-- 行程详情 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :width="dialogWidth"
      :top="isMobile ? '4vh' : '15vh'"
      class="trv-dialog"
      center
      :show-close="false"
      @opened="onDialogOpened"
      @closed="onDialogClosed"
    >
      <div v-if="selectedItem" class="trv-dialog-content">
        <button class="trv-dialog-close" @click="dialogVisible = false"><i class="el-icon-close"></i></button>

        <div class="trv-dialog-head">
          <h3 class="trv-dialog-title">{{ selectedItem.title }}</h3>
          <p class="trv-dialog-sub" v-if="selectedItem.subtitle">{{ selectedItem.subtitle }}</p>
          <p class="trv-dialog-meta">
            <span v-if="selectedItem.author">{{ selectedItem.author }}</span>
            <span>{{ selectedItem.createtime }}</span>
          </p>
        </div>

        <!-- 轨迹地图 -->
        <div class="trv-block" :class="{ 'is-fullscreen': mapFullscreen }">
          <div class="trv-block-head">
            <span class="trv-block-label">足迹 · 轨迹</span>
            <div class="trv-map-tools" v-if="trackPointCount && !mapError">
              <span class="trv-tool-group">
                <button class="trv-tool-btn" :class="{ 'is-active': mapLayer === 'normal' }" @click="setMapLayer('normal')">标准</button>
                <button class="trv-tool-btn" :class="{ 'is-active': mapLayer === 'satellite' }" @click="setMapLayer('satellite')">卫星</button>
              </span>
              <span class="trv-tool-group">
                <button class="trv-tool-btn" :class="{ 'is-active': !elevationColor || !hasElevation }" @click="setElevationColor(false)">单色</button>
                <button
                  class="trv-tool-btn"
                  :class="{ 'is-active': elevationColor && hasElevation }"
                  :disabled="!hasElevation"
                  :title="hasElevation ? '' : '该轨迹无海拔数据'"
                  @click="setElevationColor(true)"
                >海拔</button>
              </span>
              <span class="trv-tool-group" v-if="imageList.length">
                <button
                  class="trv-tool-btn"
                  :class="{ 'is-active': showPhotos }"
                  :title="showPhotos ? '隐藏地图上的照片，只看轨迹' : '在地图上显示照片'"
                  @click="togglePhotos"
                >显示图片</button>
              </span>
              <span class="trv-tool-group">
                <button class="trv-tool-btn" :class="{ 'is-active': mapFullscreen }" @click="toggleMapFullscreen">{{ mapFullscreen ? '退出全屏' : '全屏' }}</button>
              </span>
            </div>
          </div>
          <div class="trv-map-wrap">
            <div ref="travelMap" class="trv-map" v-show="trackPointCount && !mapError"></div>
            <div v-if="mapError" class="trv-map-tip">{{ mapError }}</div>
            <div class="trv-elev-legend" v-if="elevationColor && hasElevation">
              <span class="trv-elev-end">{{ Math.round(elevRange.min) }}m</span>
              <span class="trv-elev-bar">
                <i v-for="(c, i) in elevationPalette" :key="'el' + i" :style="{ background: c }"></i>
              </span>
              <span class="trv-elev-end">{{ Math.round(elevRange.max) }}m</span>
            </div>

            <!-- 点击地图照片点：在照片旁就地展开该位置的全部照片 -->
            <div
              class="trv-strip"
              v-if="activeCluster && stripPos"
              :class="{ 'is-left': stripPos.side === 'left' }"
              :style="{ left: stripPos.x + 'px', top: stripPos.y + 'px' }"
            >
              <div class="trv-strip-head">
                <span class="trv-strip-time">{{ activeCluster.label }}</span>
                <span class="trv-strip-count">{{ activeCluster.photos.length }} 张</span>
                <button class="trv-strip-close" @click="closeCluster"><i class="el-icon-close"></i></button>
              </div>
              <div class="trv-strip-body">
                <figure class="trv-strip-figure" v-for="(img, i) in activeCluster.photos" :key="'sb' + i">
                  <el-image
                    class="trv-thumb"
                    :src="mediaUrl(img.thumbUrl || img.url)"
                    fit="cover"
                    lazy
                    @click.native="openViewer(activeCluster.photos, i)"
                  >
                    <div slot="error" class="trv-thumb-error"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <figcaption class="trv-strip-cap" v-if="img.desc">{{ img.desc }}</figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>

        <!-- 照片 -->
        <div class="trv-block" v-if="showPhotos && imageList.length && (!photoLayout.hasTrackTime || photoLayout.unmatched.length)">
          <template v-if="photoLayout.hasTrackTime">
            <div class="trv-unmatched" v-if="photoLayout.unmatched.length">
              <p class="trv-unmatched-title">未与轨迹对应的照片（{{ photoLayout.unmatched.length }} 张）</p>
              <div class="trv-grid">
                <figure class="trv-figure" v-for="(img, i) in photoLayout.unmatched" :key="'un' + i">
                  <el-image
                    class="trv-cell"
                    :src="mediaUrl(img.thumbUrl || img.url)"
                    fit="cover"
                    lazy
                    @click.native="openViewer(photoLayout.unmatched, i)"
                  >
                    <div slot="error" class="trv-thumb-error"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                  <figcaption class="trv-cell-cap" v-if="img.desc">{{ img.desc }}</figcaption>
                </figure>
              </div>
            </div>
          </template>

          <div class="trv-grid" v-else>
            <figure class="trv-figure" v-for="(img, i) in imageList" :key="'img' + i">
              <el-image
                class="trv-cell"
                :src="mediaUrl(img.thumbUrl || img.url)"
                fit="cover"
                lazy
                @click.native="openViewer(imageList, i)"
              >
                <div slot="error" class="trv-thumb-error"><i class="el-icon-picture-outline"></i></div>
              </el-image>
              <figcaption class="trv-cell-cap" v-if="img.desc">{{ img.desc }}</figcaption>
            </figure>
          </div>
        </div>

        <!-- 视频 -->
        <div class="trv-block" v-if="videoList.length">
          <div class="trv-block-head">
            <span class="trv-block-label">影像 · 视频</span>
            <span class="trv-block-meta">{{ videoList.length }} 段</span>
          </div>
          <div class="trv-video-grid">
            <div class="trv-video-cell" v-for="(v, i) in videoList" :key="'vid' + i">
              <video :src="mediaUrl(v.url)" controls preload="metadata"></video>
              <span class="trv-video-name">{{ v.name || ('视频' + (i + 1)) }}</span>
            </div>
          </div>
        </div>

        <!-- 游记正文 -->
        <div class="trv-block" v-if="formatContent(selectedItem.content).length">
          <div class="trv-block-head">
            <span class="trv-block-label">行记 · 见闻</span>
          </div>
          <div class="trv-note">
            <p v-for="(line, idx) in formatContent(selectedItem.content)" :key="idx" class="trv-note-line">{{ line }}</p>
          </div>
        </div>

        <p class="trv-dialog-foot">收录于 {{ selectedItem.createtime }}</p>
      </div>
    </el-dialog>

    <!-- 图片最大化查看：照片取色虚化背景 + 描述（手机端友好） -->
    <div class="trv-viewer" v-if="viewerVisible" @click="closeViewer">
      <!-- 当前照片取色虚化背景（换图时随之切换） -->
      <div class="trv-viewer-bg" :key="'bg' + viewerIndex" :style="viewerBackdropStyle"></div>
      <div class="trv-viewer-scrim"></div>
      <button class="trv-viewer-close" @click.stop="closeViewer"><i class="el-icon-close"></i></button>
      <span class="trv-viewer-count" v-if="viewerList.length > 1" @click.stop>{{ viewerIndex + 1 }} / {{ viewerList.length }}</span>
      <button v-if="viewerList.length > 1" class="trv-viewer-nav is-prev" @click.stop="viewerPrev"><i class="el-icon-arrow-left"></i></button>
      <button v-if="viewerList.length > 1" class="trv-viewer-nav is-next" @click.stop="viewerNext"><i class="el-icon-arrow-right"></i></button>
      <div class="trv-viewer-stage">
        <img class="trv-viewer-img" :src="viewerCurrent.url" :alt="viewerCurrent.desc" @click.stop="onViewerImgClick" />
      </div>
      <p class="trv-viewer-cap" v-if="viewerCurrent.desc" @click.stop>{{ viewerCurrent.desc }}</p>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { UseUrl } from "../../utils/config";
import * as utils from "../../utils/utils";
import { loadAMap, mediaUrl as buildMediaUrl, wgs84ToGcj02 } from "../../utils/travel";

// 照片与轨迹时间匹配的容差（秒）：只容忍相机时钟的小幅误差。
const PHOTO_MATCH_TOL_SEC = 600;
// 枚举候选时区偏移的步长（秒）
const TZ_STEP_SEC = 900;
// 照片点聚类阈值（像素）：两个照片点在地图上的屏幕距离小于该值就合并为一个带角标的点，
// 随缩放级别变化而自动分合（约等于一个照片图标的大小 38px）。
const PHOTO_CLUSTER_PX = 40;
// 每公里里程碑标记的间隔（米）
const KM_MARK_STEP_M = 1000;
// 海拔着色档位配色（低 → 高）
const ELEVATION_PALETTE = ["#2c7bb6", "#26a6c9", "#39c2b4", "#8fd97f", "#f2d94e", "#f5a93b", "#ef7d2e", "#e14b2a", "#c01d1d"];
// 海拔着色时轨迹抽稀后的点数上限（避免生成过多折线）
const ELEV_MAX_POINTS = 900;

// 两点间大圆距离（米），用于沿轨迹累计里程
function haversineMeters(a, b) {
  const R = 6371000;
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * s2 * s2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export default {
  name: "travel",
  data() {
    return {
      items: [],
      displayItems: [],
      index: 0,
      limit: 9,
      total: 0,
      selectedCategory: "",
      categories: [],
      mapError: "",
      activeCluster: null,
      stripPos: null,
      stripAnchor: null,
      mapLayer: "normal",
      elevationColor: true,
      // 轨迹地图是否铺满页面（页内全屏，非系统全屏）
      mapFullscreen: false,
      // 是否显示照片（地图照片点 + 照片区块）；关闭后地图只留轨迹
      showPhotos: true,
      elevationPalette: ELEVATION_PALETTE,
      isLoading: false,
      searchKeyword: "",
      sortOrder: "desc",
      querySeq: 0,
      sortOptions: [
        { label: "新→旧", value: "desc" },
        { label: "旧→新", value: "asc" }
      ],
      dialogVisible: false,
      selectedItem: null,
      hasMore: true,
      viewerVisible: false,
      viewerList: [],
      viewerIndex: 0,
      windowWidth: (typeof window !== "undefined" ? window.innerWidth : 1280)
    };
  },
  computed: {
    viewerCurrent() {
      return this.viewerList[this.viewerIndex] || {};
    },
    // 取当前照片的小图（320px 足够，模糊后看不出细节）作为虚化背景
    viewerBackdropStyle() {
      const cur = this.viewerCurrent || {};
      const u = cur.thumb || cur.url;
      return u ? { backgroundImage: 'url("' + u + '")' } : {};
    },
    imageList() {
      return (this.selectedItem && this.selectedItem.images) || [];
    },
    videoList() {
      return (this.selectedItem && this.selectedItem.videos) || [];
    },
    trackPoints() {
      const t = this.selectedItem && this.selectedItem.track;
      return (t && t.points) || [];
    },
    trackPointCount() {
      return this.trackPoints.length;
    },
    // 海拔范围（忽略缺失/非正值的点）；无有效海拔时返回 null
    elevRange() {
      let min = Infinity;
      let max = -Infinity;
      let cnt = 0;
      this.trackPoints.forEach(p => {
        const e = Number(p && p.ele);
        if (isFinite(e) && e > 0) {
          cnt++;
          if (e < min) min = e;
          if (e > max) max = e;
        }
      });
      if (cnt < 2 || max - min < 5) return null;
      return { min: min, max: max };
    },
    hasElevation() {
      return !!this.elevRange;
    },
    // 照片与轨迹的时间对齐：每张照片按自身拍摄时间（秒级）插值经纬度；
    // 是否合并显示由地图缩放级别下的屏幕距离决定（见 drawPhotoMarkers），与时间无关。
    photoLayout() {
      const imgs = this.imageList;
      const pts = this.trackPoints
        .filter(p => p && Number(p.time) > 0)
        .sort((a, b) => a.time - b.time);
      if (!imgs.length || pts.length < 2) {
        return { hasTrackTime: false, points: [], unmatched: [], offset: 0 };
      }
      const tMin = pts[0].time;
      const tMax = pts[pts.length - 1].time;
      const withTime = [];
      const unmatched = [];
      imgs.forEach(img => {
        if (Number(img.time) > 0) { withTime.push(img); } else { unmatched.push(img); }
      });
      const offset = this.detectTimeOffset(withTime, tMin, tMax);
      // 编辑行程里配置的人工校正（秒）：正值 = 照片沿轨迹后移，负值 = 前移
      const corr = Number(this.selectedItem && this.selectedItem.photoTimeOffset) || 0;
      const tol = PHOTO_MATCH_TOL_SEC;
      const points = [];
      withTime.forEach(img => {
        const t = img.time - offset + corr;
        if (t < tMin - tol || t > tMax + tol) { unmatched.push(img); return; }
        const position = this.interpolateTrack(pts, t);
        if (!position) { unmatched.push(img); return; }
        points.push({ photo: img, time: t, position: position });
      });
      points.sort((a, b) => a.time - b.time);
      return { hasTrackTime: true, points: points, unmatched: unmatched, offset: offset };
    },
    // 按收录时间排序 → 按年份分组
    timelineGroups: function () {
      const dir = this.sortOrder === "asc" ? 1 : -1;
      const sorted = [...this.displayItems].sort((a, b) => {
        return ((a.createTime || 0) - (b.createTime || 0)) * dir;
      });
      const groups = [];
      const bucket = {};
      sorted.forEach(item => {
        const year = item.createTime
          ? new Date(item.createTime * 1000).getFullYear() + ""
          : "未知";
        if (!bucket[year]) {
          bucket[year] = { year: year, items: [] };
          groups.push(bucket[year]);
        }
        bucket[year].items.push(item);
      });
      return groups;
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    dialogWidth() {
      return this.isMobile ? "94%" : "900px";
    }
  },
  watch: {
    sortOrder: function () {
      this.resetQuery();
    },
    dialogVisible: function (val) {
      if (!val) {
        this.destroyTravelMap();
      }
    },
    items: {
      handler(newItems) {
        this.displayItems = newItems;
      },
      deep: false
    }
  },
  methods: {
    queryTravel: function () {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      const seq = this.querySeq;
      const params = new URLSearchParams();
      params.append('start', this.index);
      params.append('limit', this.limit);
      params.append('order', this.sortOrder);
      if (this.selectedCategory) {
        params.append('title', this.selectedCategory);
      }
      const kw = (this.searchKeyword || '').trim();
      if (kw) {
        params.append('keyword', kw);
      }
      var that = this;
      Vue.axios.post(UseUrl + "/travel/TravelFind", params).then((response) => {
        if (seq !== that.querySeq) {
          that.isLoading = false;
          that.queryTravel();
          return;
        }
        var curItem = response.data.Item;
        if (typeof response.data.total === 'number') {
          that.total = response.data.total;
        }
        if (!curItem || curItem.length === 0) {
          that.hasMore = false;
          that.isLoading = false;
          return;
        }
        for (var i = 0; i < curItem.length; i++) {
          curItem[i]["createtime"] = utils.timestampToTime(curItem[i]["createTime"]);
          that.items.push(curItem[i]);
        }
        that.index += curItem.length;
        that.isLoading = false;
        that.displayItems = [...that.items];
        if (curItem.length < that.limit) {
          that.hasMore = false;
        }
      }).catch((error => {
        console.log(error);
        that.isLoading = false;
      }))
    },
    resetQuery: function () {
      this.querySeq++;
      this.items = [];
      this.displayItems = [];
      this.index = 0;
      this.hasMore = true;
      if (!this.isLoading) {
        this.queryTravel();
      }
    },
    queryCategories: function () {
      var that = this;
      Vue.axios.post(UseUrl + "/travel/categories").then((response) => {
        that.categories = response.data.categories || [];
      }).catch((error) => {
        console.log(error);
      })
    },
    onCategoryChange: function () {
      this.resetQuery();
    },
    onSearch: function () {
      if (this._searchTimer) {
        clearTimeout(this._searchTimer);
      }
      this._searchTimer = setTimeout(() => {
        this.resetQuery();
      }, 300);
    },
    formatContent: function (content) {
      if (!content) return [];
      return content.split(/[\n\r]/).filter(line => line.trim() !== '');
    },
    excerpt: function (text, n) {
      if (!text) return "";
      const oneLine = String(text).replace(/[\n\r]+/g, " / ");
      return oneLine.length > n ? oneLine.slice(0, n) + "…" : oneLine;
    },
    coverOf: function (item) {
      const imgs = (item && item.images) || [];
      if (!imgs.length) return "";
      return imgs[0].thumbUrl || imgs[0].url;
    },
    trackCount: function (item) {
      const t = item && item.track;
      return (t && t.points && t.points.length) || 0;
    },
    imageCount: function (item) {
      return ((item && item.images) || []).length;
    },
    videoCount: function (item) {
      return ((item && item.videos) || []).length;
    },
    openDetail: function (item) {
      this.selectedItem = item;
      this.mapError = "";
      this.activeCluster = null;
      this.stripAnchor = null;
      this.stripPos = null;
      // 每次打开详情默认显示图片
      this.showPhotos = true;
      this.dialogVisible = true;
    },
    onDialogOpened: function () {
      if (this.selectedItem) {
        this.renderTravelMap(this.selectedItem);
      }
    },
    onDialogClosed: function () {
      this.closeViewer();
      this.destroyTravelMap();
    },
    // 用高德地图绘制轨迹，并以轨迹为展示区域自动缩放
    renderTravelMap: function (item) {
      const points = (item && item.track && item.track.points) || [];
      if (!points.length) {
        this.mapError = "该行程暂无轨迹数据";
        return;
      }
      const el = this.$refs.travelMap;
      if (!el) {
        this.mapError = "地图容器未就绪";
        return;
      }
      loadAMap().then((AMap) => {
        if (this.selectedItem !== item || !this.$refs.travelMap) {
          return;
        }
        const map = new AMap.Map(el, {
          zoom: 12,
          center: wgs84ToGcj02(points[0].lng, points[0].lat),
          viewMode: "2D"
        });
        this.amapInstance = map;
        this.amapCtor = AMap;
        // 轨迹是 WGS-84，高德底图是 GCJ-02，渲染前统一换算
        const path = points.map(p => wgs84ToGcj02(p.lng, p.lat));
        this.trackPathGcj = path;
        this.trackPts = points;
        this.applyMapLayer();
        this.drawTrack();
        this.drawKmMarkers();
        map.add(new AMap.Marker({ position: path[0], label: { content: "起", direction: "top" } }));
        map.add(new AMap.Marker({ position: path[path.length - 1], label: { content: "终", direction: "top" } }));
        map.on("mapmove", this.updateStripPos);
        map.on("zoomchange", this.updateStripPos);
        map.on("moveend", this.updateStripPos);
        map.on("zoomend", this.onMapZoomEnd);
        map.setFitView(this.trackLines, false, [50, 50, 50, 50]);
        this.$nextTick(() => {
          if (this.amapInstance !== map) return;
          try {
            map.resize();
            map.setFitView(this.trackLines, false, [50, 50, 50, 50]);
          } catch (e) { /* ignore */ }
          // 等地图尺寸与视野稳定后再按当前缩放聚类照片点
          this.drawPhotoMarkers();
        });
      }).catch((err) => {
        this.mapError = (err && err.message) || "地图加载失败";
        console.log("amap load error", err);
      });
    },
    // 依据当前缩放级别，把照片点按屏幕像素距离聚类：
    // 距离小于 PHOTO_CLUSTER_PX 的照片合并为一个点（角标显示张数），放大后自然分开。
    buildPhotoClusters(points, thresholdPx) {
      const map = this.amapInstance;
      if (!map || !points.length) return [];
      const items = points.map(p => {
        const gcj = wgs84ToGcj02(p.position.lng, p.position.lat);
        let x = 0;
        let y = 0;
        try {
          const px = map.lngLatToContainer(gcj);
          x = px.x;
          y = px.y;
        } catch (e) { /* 地图未就绪时按 (0,0) 处理，随后会重绘 */ }
        return { point: p, gcj: gcj, x: x, y: y };
      });
      const clusters = [];
      items.forEach(it => {
        let hit = null;
        for (let i = 0; i < clusters.length; i++) {
          const c = clusters[i];
          const dx = it.x - c.cx;
          const dy = it.y - c.cy;
          if (dx * dx + dy * dy <= thresholdPx * thresholdPx) { hit = c; break; }
        }
        if (!hit) {
          hit = { items: [], cx: 0, cy: 0 };
          clusters.push(hit);
        }
        hit.items.push(it);
        hit.cx = hit.items.reduce((s, k) => s + k.x, 0) / hit.items.length;
        hit.cy = hit.items.reduce((s, k) => s + k.y, 0) / hit.items.length;
      });
      const offset = this.photoLayout.offset;
      return clusters.map(c => {
        const lng = c.items.reduce((s, k) => s + k.point.position.lng, 0) / c.items.length;
        const lat = c.items.reduce((s, k) => s + k.point.position.lat, 0) / c.items.length;
        const times = c.items.map(k => k.point.time);
        return {
          photos: c.items.map(k => k.point.photo),
          position: { lng: lng, lat: lat },
          label: this.formatClusterLabel(times, offset)
        };
      });
    },
    // 重建地图上的照片点（数量随缩放变化，故每次按当前缩放重新聚类）
    drawPhotoMarkers() {
      const map = this.amapInstance;
      const AMap = this.amapCtor;
      if (!map || !AMap) return;
      (this.photoMarkers || []).forEach(m => {
        try { map.remove(m); } catch (e) { /* ignore */ }
      });
      this.photoMarkers = [];
      // 关闭「显示图片」时只保留轨迹，不绘制照片点
      if (!this.showPhotos) return;
      this.buildPhotoClusters(this.photoLayout.points || [], PHOTO_CLUSTER_PX).forEach(c => {
        if (!c.position) return;
        const cover = c.photos[0];
        const src = this.mediaUrl(cover.thumbUrl || cover.url);
        const count = c.photos.length;
        const badge = count > 1
          ? '<span style="position:absolute;right:-6px;top:-6px;min-width:16px;height:16px;line-height:16px;' +
            'padding:0 4px;box-sizing:border-box;text-align:center;font-size:11px;color:#fffdf8;background:#0f8b8d;' +
            'border-radius:9px;box-shadow:0 1px 3px rgba(0,0,0,.35);">' + count + '</span>'
          : '';
        const html =
          '<div style="position:relative;width:38px;height:38px;cursor:pointer;">' +
            '<div style="width:100%;height:100%;border:2px solid #fffdf8;border-radius:6px;overflow:hidden;' +
              'background:#e7f2f1;box-shadow:0 2px 8px rgba(15,76,82,.4);">' +
              '<img src="' + src + '" style="width:100%;height:100%;object-fit:cover;display:block;" />' +
            '</div>' + badge +
          '</div>';
        const marker = new AMap.Marker({
          position: wgs84ToGcj02(c.position.lng, c.position.lat),
          content: html,
          offset: new AMap.Pixel(-19, -19),
          zIndex: 120,
          title: c.label + " · " + count + " 张"
        });
        marker.on("click", () => {
          // 只有一张照片时直接打开查看器，不必先展开浮层
          if (c.photos && c.photos.length === 1) {
            this.closeCluster();
            this.openViewer(c.photos, 0);
            return;
          }
          this.openCluster(c);
        });
        map.add(marker);
        this.photoMarkers.push(marker);
      });
    },
    // 显示 / 隐藏图片：关闭后地图上的照片点与照片区块一并收起，只留轨迹
    togglePhotos() {
      this.showPhotos = !this.showPhotos;
      if (!this.showPhotos) this.closeCluster();
      this.drawPhotoMarkers();
    },
    // 缩放结束后重新聚类重绘（放大→分点，缩小→合并）
    onMapZoomEnd() {
      this.drawPhotoMarkers();
      this.updateStripPos();
    },
    // 点击地图照片点：在照片旁展开该点的照片列表
    openCluster(cluster) {
      const map = this.amapInstance;
      const AMap = this.amapCtor;
      this.activeCluster = cluster;
      if (!map || !AMap || !cluster || !cluster.position) return;
      const pos = wgs84ToGcj02(cluster.position.lng, cluster.position.lat);
      this.stripAnchor = pos;
      // 把照片点移到视口中央，保证浮层四周都有空间
      try { map.setCenter(pos); } catch (e) { /* ignore */ }
      this.updateStripPos();
      this.$nextTick(this.updateStripPos);
    },
    closeCluster() {
      this.activeCluster = null;
      this.stripAnchor = null;
      this.stripPos = null;
    },
    // 轨迹地图页内全屏：切换后重算地图尺寸并重新适配视野
    toggleMapFullscreen() {
      this.mapFullscreen = !this.mapFullscreen;
      this.closeCluster();
      this.$nextTick(() => {
        const map = this.amapInstance;
        if (!map) return;
        try {
          map.resize();
          if (this.trackLines && this.trackLines.length) {
            map.setFitView(this.trackLines, false, [50, 50, 50, 50]);
          }
        } catch (e) { /* ignore */ }
        this.drawPhotoMarkers();
      });
    },
    // 依据照片点在地图容器内的像素位置，计算浮层坐标（右侧放不下则翻到左侧）
    updateStripPos() {
      const map = this.amapInstance;
      if (!map || !this.stripAnchor) {
        this.stripPos = null;
        return;
      }
      const size = map.getSize();
      const px = map.lngLatToContainer(this.stripAnchor);
      const POP_W = 236;
      const GAP = 18;
      let x = px.x + GAP;
      let side = "right";
      if (x + POP_W > size.width - 8) {
        x = px.x - GAP - POP_W;
        side = "left";
      }
      if (x < 8) x = 8;
      let y = px.y;
      if (y < 8) y = 8;
      if (y > size.height - 8) y = size.height - 8;
      this.stripPos = { x: x, y: y, side: side };
    },
    // ===== 底图图层切换：标准 / 卫星（卫星图叠加路网注记） =====
    applyMapLayer() {
      const map = this.amapInstance;
      const AMap = this.amapCtor;
      if (!map || !AMap) return;
      if (!this.satelliteLayer) {
        this.satelliteLayer = new AMap.TileLayer.Satellite();
        this.roadNetLayer = new AMap.TileLayer.RoadNet();
      }
      if (this.mapLayer === "satellite") {
        map.add(this.satelliteLayer);
        map.add(this.roadNetLayer);
      } else {
        map.remove(this.satelliteLayer);
        map.remove(this.roadNetLayer);
      }
    },
    setMapLayer(layer) {
      if (layer === this.mapLayer) return;
      this.mapLayer = layer;
      this.applyMapLayer();
    },
    setElevationColor(on) {
      if (on === this.elevationColor) return;
      if (on && !this.hasElevation) return;
      this.elevationColor = on;
      this.drawTrack();
    },
    // 重绘轨迹：单色 或 按海拔分段着色
    drawTrack() {
      const map = this.amapInstance;
      const AMap = this.amapCtor;
      const path = this.trackPathGcj;
      if (!map || !AMap || !path || !path.length) return;
      (this.trackLines || []).forEach(l => { try { map.remove(l); } catch (e) { /* ignore */ } });
      this.trackLines = [];
      if (this.elevationColor && this.hasElevation) {
        this.buildElevationPolylines().forEach(l => { map.add(l); this.trackLines.push(l); });
      } else {
        const poly = new AMap.Polyline({
          path: path,
          strokeColor: "#0f8b8d",
          strokeWeight: 5,
          strokeOpacity: 0.9,
          lineJoin: "round",
          showDir: true,
          zIndex: 50
        });
        map.add(poly);
        this.trackLines.push(poly);
      }
    },
    // 按海拔把轨迹切成若干彩色折线：相邻同档点合并为一段
    buildElevationPolylines() {
      const AMap = this.amapCtor;
      const path = this.trackPathGcj;
      const pts = this.trackPts || [];
      const range = this.elevRange;
      if (!AMap || !path || !path.length || !range) return [];
      const n = path.length;
      const span = (range.max - range.min) || 1;
      const pal = ELEVATION_PALETTE;
      const bandOf = (ele) => {
        const e = Number(ele);
        let v = (isFinite(e) && e > 0 ? e : range.min) - range.min;
        if (v < 0) v = 0;
        if (v > span) v = span;
        let idx = Math.floor((v / span) * pal.length);
        if (idx >= pal.length) idx = pal.length - 1;
        return idx;
      };
      const step = Math.max(1, Math.ceil(n / ELEV_MAX_POINTS));
      const lines = [];
      let curBand = bandOf(pts[0] && pts[0].ele);
      let seg = [path[0]];
      for (let i = step; i < n; i += step) {
        seg.push(path[i]);
        const b = bandOf(pts[i] && pts[i].ele);
        if (b !== curBand) {
          lines.push(new AMap.Polyline({
            path: seg.slice(), strokeColor: pal[curBand], strokeWeight: 5,
            strokeOpacity: 0.95, lineJoin: "round", zIndex: 50
          }));
          seg = [path[i - step], path[i]];
          curBand = b;
        }
      }
      if ((n - 1) % step !== 0) seg.push(path[n - 1]);
      if (seg.length > 1) {
        lines.push(new AMap.Polyline({
          path: seg, strokeColor: pal[curBand], strokeWeight: 5,
          strokeOpacity: 0.95, lineJoin: "round", zIndex: 50
        }));
      }
      return lines;
    },
    // 每公里里程碑标记：沿轨迹累计里程，每满 1km 打一个 "xkm" 标签
    drawKmMarkers() {
      const map = this.amapInstance;
      const AMap = this.amapCtor;
      const path = this.trackPathGcj;
      if (!map || !AMap || !path || path.length < 2) return;
      (this.kmMarkers || []).forEach(m => { try { map.remove(m); } catch (e) { /* ignore */ } });
      this.kmMarkers = [];
      let acc = 0;
      let next = KM_MARK_STEP_M;
      for (let i = 1; i < path.length; i++) {
        const a = path[i - 1];
        const b = path[i];
        const d = haversineMeters(a, b);
        while (acc + d >= next) {
          const ratio = (next - acc) / (d || 1);
          const pos = [a[0] + (b[0] - a[0]) * ratio, a[1] + (b[1] - a[1]) * ratio];
          const km = Math.round(next / KM_MARK_STEP_M);
          const marker = new AMap.Marker({
            position: pos,
            zIndex: 115,
            title: km + " 公里",
            content: '<div style="transform:translate(-50%,-50%);padding:1px 7px;font-size:11px;' +
              'color:#fffdf8;background:rgba(15,139,141,.92);border:1px solid #fffdf8;border-radius:10px;' +
              'white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.35);">' + km + 'km</div>'
          });
          map.add(marker);
          this.kmMarkers.push(marker);
          next += KM_MARK_STEP_M;
        }
        acc += d;
      }
    },
    destroyTravelMap: function () {
      if (this.amapInstance) {
        try { this.amapInstance.destroy(); } catch (e) { /* ignore */ }
        this.amapInstance = null;
      }
      this.amapCtor = null;
      this.photoMarkers = [];
      this.kmMarkers = [];
      this.trackLines = [];
      this.satelliteLayer = null;
      this.roadNetLayer = null;
      this.trackPathGcj = null;
      this.trackPts = [];
      this.activeCluster = null;
      this.stripAnchor = null;
      this.stripPos = null;
      this.mapFullscreen = false;
    },
    // 自动识别相机时区偏移：EXIF 本地时间（无时区）与轨迹 UTC 对齐
    detectTimeOffset(imgs, tMin, tMax) {
      if (!imgs.length) return 0;
      const tol = PHOTO_MATCH_TOL_SEC;
      const localOffset = -new Date().getTimezoneOffset() * 60;
      let best = null;
      for (let off = -12 * 3600; off <= 14 * 3600; off += TZ_STEP_SEC) {
        let hit = 0;
        for (let i = 0; i < imgs.length; i++) {
          const t = imgs[i].time - off;
          if (t >= tMin - tol && t <= tMax + tol) hit++;
        }
        const cand = {
          off: off,
          hit: hit,
          wholeHour: off % 3600 === 0,
          localDiff: Math.abs(off - localOffset)
        };
        if (!best) { best = cand; continue; }
        if (cand.hit !== best.hit) {
          if (cand.hit > best.hit) best = cand;
          continue;
        }
        if (cand.wholeHour !== best.wholeHour) {
          if (cand.wholeHour) best = cand;
          continue;
        }
        if (cand.localDiff !== best.localDiff) {
          if (cand.localDiff < best.localDiff) best = cand;
          continue;
        }
        if (Math.abs(cand.off) < Math.abs(best.off)) best = cand;
      }
      return best ? best.off : 0;
    },
    // 在按时间排序的轨迹点上按时间线性插值出经纬度
    interpolateTrack(pts, t) {
      const n = pts.length;
      if (!n) return null;
      if (t <= pts[0].time) return { lng: pts[0].lng, lat: pts[0].lat };
      if (t >= pts[n - 1].time) return { lng: pts[n - 1].lng, lat: pts[n - 1].lat };
      let lo = 0;
      let hi = n - 1;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (pts[mid].time <= t) { lo = mid; } else { hi = mid; }
      }
      const a = pts[lo];
      const b = pts[hi];
      const span = (b.time - a.time) || 1;
      const r = (t - a.time) / span;
      return { lng: a.lng + (b.lng - a.lng) * r, lat: a.lat + (b.lat - a.lat) * r };
    },
    // 时钟文案：用拍摄地本地时间显示（时间对齐后仍是 UTC，故加回偏移）
    formatClock(sec, offset, withSec) {
      const d = new Date((sec + (offset || 0)) * 1000);
      const p = v => (v < 10 ? "0" + v : v);
      let s = p(d.getUTCHours()) + ":" + p(d.getUTCMinutes());
      if (withSec) s += ":" + p(d.getUTCSeconds());
      return s;
    },
    // 照片点标签：同一位置的合影显示时间跨度，单张显示到秒
    formatClusterLabel(times, offset) {
      if (!times || !times.length) return "";
      const lo = Math.min.apply(null, times);
      const hi = Math.max.apply(null, times);
      if (times.length === 1 || hi - lo < 1) return this.formatClock(lo, offset, true);
      return this.formatClock(lo, offset) + " – " + this.formatClock(hi, offset);
    },
    // ===== 图片最大化查看层（列表为原始媒体数组，携带 desc） =====
    openViewer(list, index) {
      const items = (list || [])
        .filter(p => p && p.url)
        .map(p => ({
          url: this.mediaUrl(p.url),
          thumb: this.mediaUrl(p.thumbUrl || p.url),
          desc: p.desc || ""
        }));
      if (!items.length) return;
      const i = Math.min(Math.max(index || 0, 0), items.length - 1);
      this.viewerList = items;
      this.viewerIndex = i;
      this.viewerVisible = true;
    },
    closeViewer() {
      this.viewerVisible = false;
    },
    viewerPrev() {
      if (this.viewerList.length < 2) return;
      this.viewerIndex = (this.viewerIndex - 1 + this.viewerList.length) % this.viewerList.length;
    },
    viewerNext() {
      if (this.viewerList.length < 2) return;
      this.viewerIndex = (this.viewerIndex + 1) % this.viewerList.length;
    },
    onViewerKey(e) {
      if (!this.viewerVisible) return;
      if (e.key === "Escape") this.closeViewer();
      else if (e.key === "ArrowLeft") this.viewerPrev();
      else if (e.key === "ArrowRight") this.viewerNext();
    },
    // 图片按 contain 铺满舞台，四周可能有留白；点击留白处等同于点背景关闭
    onViewerImgClick(e) {
      const el = e.currentTarget;
      const nw = el.naturalWidth;
      const nh = el.naturalHeight;
      const bw = el.clientWidth;
      const bh = el.clientHeight;
      if (!nw || !nh) {
        this.closeViewer();
        return;
      }
      const scale = Math.min(bw / nw, bh / nh);
      const rw = nw * scale;
      const rh = nh * scale;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const left = (bw - rw) / 2;
      const top = (bh - rh) / 2;
      if (x < left || x > left + rw || y < top || y > top + rh) {
        this.closeViewer();
      }
    },
    mediaUrl: function (url) {
      return buildMediaUrl(url, UseUrl);
    },
    formatDay: function (item) {
      if (!item.createTime) return "——";
      const d = new Date(item.createTime * 1000);
      const p = v => (v < 10 ? "0" + v : v);
      return d.getFullYear() + "." + p(d.getMonth() + 1) + "." + p(d.getDate());
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    }
  },
  mounted() {
    this.queryCategories();
    this.queryTravel();
    window.addEventListener("resize", this.onResize, false);
    window.addEventListener("keydown", this.onViewerKey, false);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize, false);
    window.removeEventListener("keydown", this.onViewerKey, false);
    this.destroyTravelMap();
  },
  beforeCreate() {
    this.index = 0;
    this.limit = 9;
    this.items = [];
    this.displayItems = [];
    this.total = 0;
    this.selectedCategory = "";
    this.categories = [];
    this.isLoading = false;
  }
}
</script>

<style scoped>
/* ============ 基调：远山 · 海蓝 · 暖沙（旅行手帐） ============ */
.travel-container {
  --sand: #f6f1e6;
  --card: #fffdf8;
  --deep: #0f4c52;
  --teal: #0f8b8d;
  --teal-soft: #58a7a6;
  --amber: #e08a3c;
  --amber-deep: #c46f22;
  --ink: #1e2b2e;
  --ink-soft: #566a6b;
  --ink-faint: #93a3a2;
  --hairline: #dcd3c1;
  --font-display: "Noto Serif SC", "Source Han Serif SC", "Songti SC", STSong, SimSun, serif;

  max-width: 1120px;
  margin: 0 auto;
  padding: 26px 16px 64px;
  color: var(--ink);
  font-family: var(--font-display);
  background-color: transparent;
  background-image:
    radial-gradient(1000px 460px at 12% -8%, rgba(255, 253, 248, 0.9), transparent 62%),
    radial-gradient(760px 420px at 100% 0%, rgba(15, 139, 141, 0.08), transparent 58%),
    radial-gradient(680px 340px at 50% 108%, rgba(224, 138, 60, 0.08), transparent 60%);
}

/* ============ 题头 ============ */
.trv-header {
  text-align: center;
  padding: 18px 0 6px;
  margin-bottom: 18px;
}

.trv-route {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 14px;
}

.trv-route .trv-dot {
  width: 7px;
  height: 7px;
  border: 2px solid var(--teal);
  border-radius: 50%;
  background: var(--card);
}

.trv-route i {
  width: 70px;
  height: 0;
  border-top: 2px dashed rgba(15, 139, 141, 0.5);
}

.trv-route .trv-plane {
  font-size: 20px;
  color: var(--amber);
  transform: translateY(-1px);
}

.trv-title {
  margin: 0;
  font-size: 38px;
  font-weight: 700;
  letter-spacing: 10px;
  text-indent: 10px;
  color: var(--deep);
}

.trv-sub {
  margin: 12px 0 0;
  color: var(--ink-faint);
  font-size: 14px;
  letter-spacing: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.trv-link {
  color: var(--teal);
  text-decoration: none;
  padding: 3px 14px;
  border: 1px solid rgba(15, 139, 141, 0.5);
  border-radius: 20px;
  letter-spacing: 2px;
  font-size: 13px;
  transition: all 0.25s ease;
}

.trv-link:hover {
  background: var(--teal);
  color: #fffdf8;
  box-shadow: 0 4px 12px rgba(15, 139, 141, 0.28);
}

/* ============ 筛选区 ============ */
.trv-filter {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  margin: 0 auto 26px;
  background: rgba(255, 253, 248, 0.85);
  border: 1px solid var(--hairline);
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(15, 76, 82, 0.05);
}

.trv-search {
  flex: 1;
  min-width: 0;
}

.trv-select {
  width: 190px;
}

.trv-sort {
  display: inline-flex;
  border: 1px solid rgba(15, 139, 141, 0.45);
  border-radius: 20px;
  overflow: hidden;
}

.trv-sort button {
  font-family: var(--font-display);
  font-size: 13px;
  letter-spacing: 2px;
  color: var(--teal);
  background: transparent;
  border: none;
  padding: 7px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.trv-sort button + button {
  border-left: 1px solid rgba(15, 139, 141, 0.3);
}

.trv-sort button:hover {
  background: rgba(15, 139, 141, 0.08);
}

.trv-sort button.is-active {
  background: var(--teal);
  color: #fffdf8;
}

.trv-stats {
  margin-left: auto;
  color: var(--ink-faint);
  font-size: 13px;
  letter-spacing: 1px;
  white-space: nowrap;
}

/* ============ 年份分组 ============ */
.trv-year + .trv-year {
  margin-top: 36px;
}

.trv-year-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 16px;
}

.trv-year-num {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 3px;
  color: var(--deep);
}

.trv-year-rule {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(15, 139, 141, 0.45), transparent);
}

.trv-year-count {
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

/* ============ 旅程卡 ============ */
.trv-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.trv-card {
  display: flex;
  flex-direction: column;
  background: var(--card);
  border: 1px solid var(--hairline);
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease;
  box-shadow: 0 3px 12px rgba(15, 76, 82, 0.06);
  animation: trv-rise 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

@keyframes trv-rise {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.trv-card:hover {
  transform: translateY(-5px);
  border-color: rgba(15, 139, 141, 0.55);
  box-shadow: 0 14px 30px rgba(15, 76, 82, 0.16);
}

.trv-cover {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(15, 139, 141, 0.85), rgba(224, 138, 60, 0.7));
}

.trv-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}

.trv-card:hover .trv-cover img {
  transform: scale(1.07);
}

.trv-cover-empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fffdf8;
  font-size: 46px;
  letter-spacing: 0;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
}

.trv-cover-veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(15, 76, 82, 0) 42%, rgba(15, 76, 82, 0.42) 100%);
  pointer-events: none;
}

.trv-date {
  position: absolute;
  top: 10px;
  left: 10px;
  padding: 3px 10px;
  font-size: 12px;
  letter-spacing: 1px;
  color: #fffdf8;
  background: rgba(15, 76, 82, 0.62);
  border-radius: 20px;
  backdrop-filter: blur(3px);
}

.trv-card-body {
  display: flex;
  flex-direction: column;
  padding: 15px 16px 14px;
}

.trv-card-title {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--ink);
}

.trv-card-sub {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 1px;
}

.trv-card-excerpt {
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.8;
  color: var(--ink-soft);
  min-height: 46px;
}

.trv-card-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 10px;
  border-top: 1px dashed var(--hairline);
  font-size: 12px;
  color: var(--ink-faint);
}

.trv-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  letter-spacing: 1px;
}

.trv-open {
  margin-left: auto;
  color: var(--teal);
  letter-spacing: 1px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  opacity: 0.8;
  transition: gap 0.2s, opacity 0.2s;
}

.trv-card:hover .trv-open {
  opacity: 1;
  gap: 7px;
}

/* ============ 空态 ============ */
.trv-empty {
  text-align: center;
  padding: 70px 0 40px;
  color: var(--ink-faint);
  letter-spacing: 3px;
}

.trv-empty-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 14px;
  font-size: 16px;
  letter-spacing: 2px;
  color: var(--teal);
  border: 2px dashed rgba(15, 139, 141, 0.5);
  border-radius: 50%;
}

/* ============ 加载更多 ============ */
.trv-more {
  text-align: center;
  padding: 32px 0 8px;
}

.trv-more-btn {
  font-family: var(--font-display);
  font-size: 15px;
  letter-spacing: 5px;
  text-indent: 5px;
  color: var(--teal);
  background: transparent;
  border: 1px solid rgba(15, 139, 141, 0.55);
  border-radius: 24px;
  padding: 10px 46px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.trv-more-btn:hover {
  background: var(--teal);
  color: #fffdf8;
  box-shadow: 0 6px 18px rgba(15, 139, 141, 0.3);
}

.trv-more-btn.is-loading {
  opacity: 0.6;
  cursor: wait;
}

/* ============ 详情弹窗 ============ */
.trv-dialog >>> .el-dialog {
  border-radius: 16px;
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--hairline);
  box-shadow: 0 22px 60px rgba(15, 76, 82, 0.28);
}

.trv-dialog >>> .el-dialog__header {
  display: none;
}

.trv-dialog >>> .el-dialog__body {
  padding: 0;
}

.trv-dialog-content {
  position: relative;
  padding: 34px 40px 26px;
  background:
    radial-gradient(560px 200px at 50% 0%, rgba(15, 139, 141, 0.08), transparent 70%),
    var(--card);
}

.trv-dialog-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-faint);
  font-size: 18px;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
}

.trv-dialog-close:hover {
  color: var(--teal);
  background: rgba(15, 139, 141, 0.1);
}

.trv-dialog-head {
  text-align: center;
  margin-bottom: 22px;
}

.trv-dialog-title {
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  letter-spacing: 4px;
  color: var(--deep);
}

.trv-dialog-sub {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

.trv-dialog-meta {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.trv-dialog-meta span + span::before {
  content: "·";
  margin-right: 14px;
  color: var(--teal-soft);
}

/* ============ 区块 ============ */
.trv-block + .trv-block {
  margin-top: 24px;
}

.trv-block-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 7px;
  border-bottom: 1px dashed var(--hairline);
}

.trv-block-label {
  font-size: 15px;
  color: var(--deep);
  letter-spacing: 4px;
  position: relative;
  padding-left: 14px;
}

.trv-block-label::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 6px;
  height: 6px;
  background: var(--amber);
  border-radius: 50%;
}

.trv-block-meta {
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 1px;
}

/* ============ 地图 ============ */
.trv-map-wrap {
  position: relative;
  border: 1px solid var(--hairline);
  border-radius: 12px;
  overflow: hidden;
  background: rgba(15, 139, 141, 0.06);
}

.trv-map {
  width: 100%;
  height: 380px;
}

/* 页内全屏：轨迹区块铺满可视区域（非系统全屏） */
.trv-block.is-fullscreen {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 3000;
  margin: 0;
  padding: 14px 18px 18px;
  display: flex;
  flex-direction: column;
  background: var(--card);
}

.trv-block.is-fullscreen .trv-block-head {
  flex: 0 0 auto;
}

.trv-block.is-fullscreen .trv-map-wrap {
  flex: 1 1 auto;
  min-height: 0;
  border-radius: 10px;
}

.trv-block.is-fullscreen .trv-map {
  height: 100%;
}

.trv-map-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 40px 20px;
  font-size: 13px;
  color: var(--ink-faint);
  letter-spacing: 2px;
  text-align: center;
}

/* ============ 地图工具（图层 / 海拔着色） ============ */
.trv-map-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.trv-tool-group {
  display: inline-flex;
  border: 1px solid rgba(15, 139, 141, 0.35);
  border-radius: 16px;
  overflow: hidden;
}

.trv-tool-btn {
  font-size: 12px;
  letter-spacing: 1px;
  color: var(--teal);
  background: transparent;
  border: none;
  padding: 3px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.trv-tool-btn + .trv-tool-btn {
  border-left: 1px solid rgba(15, 139, 141, 0.25);
}

.trv-tool-btn:hover:not(:disabled) {
  background: rgba(15, 139, 141, 0.08);
}

.trv-tool-btn.is-active {
  background: var(--teal);
  color: #fffdf8;
}

.trv-tool-btn:disabled {
  color: var(--ink-faint);
  cursor: not-allowed;
  opacity: 0.5;
}

.trv-elev-legend {
  position: absolute;
  left: 12px;
  bottom: 12px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-size: 11px;
  color: var(--deep);
  background: rgba(255, 253, 248, 0.88);
  border: 1px solid var(--hairline);
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(15, 76, 82, 0.18);
}

.trv-elev-bar {
  display: inline-flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
}

.trv-elev-bar > i {
  display: block;
  width: 14px;
  height: 100%;
}

.trv-elev-end {
  letter-spacing: 0.5px;
}

/* ============ 照片浮层（点击地图照片点后在照片旁展开） ============ */
.trv-strip {
  position: absolute;
  z-index: 30;
  width: 236px;
  max-height: 300px;
  display: flex;
  flex-direction: column;
  transform: translateY(-50%);
  border: 1px solid var(--hairline);
  border-radius: 10px;
  background: rgba(255, 253, 248, 0.97);
  box-shadow: 0 6px 20px rgba(15, 76, 82, 0.25);
  overflow: hidden;
  animation: trv-fade 0.2s ease both;
}

@keyframes trv-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.trv-strip-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px dashed var(--hairline);
}

.trv-strip-time {
  font-size: 13px;
  color: var(--teal);
  letter-spacing: 1px;
}

.trv-strip-count {
  font-size: 12px;
  color: var(--ink-faint);
}

.trv-strip-close {
  margin-left: auto;
  border: none;
  background: transparent;
  color: var(--ink-faint);
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s;
}

.trv-strip-close:hover {
  color: var(--teal);
}

.trv-strip-body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 8px;
  padding: 10px;
  overflow-y: auto;
}

.trv-strip-figure {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 86px;
  margin: 0;
}

.trv-strip-cap {
  width: 100%;
  font-size: 11px;
  line-height: 1.5;
  color: var(--ink-soft);
  letter-spacing: 0.5px;
  text-align: center;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.trv-thumb {
  flex: 0 0 auto;
  width: 86px;
  height: 86px;
  border: 2px solid #fffdf8;
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
  box-shadow: 0 2px 8px rgba(15, 76, 82, 0.14);
}

.trv-thumb >>> .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.trv-thumb:hover >>> .el-image__inner {
  transform: scale(1.08);
}

/* ============ 图片栅格 ============ */
.trv-unmatched {
  margin-top: 6px;
}

.trv-unmatched-title {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

.trv-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.trv-cell {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border: 1px solid var(--hairline);
  border-radius: 8px;
  background: rgba(15, 139, 141, 0.06);
}

.trv-figure {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
}

.trv-cell-cap {
  font-size: 12px;
  line-height: 1.5;
  color: var(--ink-soft);
  letter-spacing: 1px;
  word-break: break-word;
}

.trv-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.trv-cell:hover img {
  transform: scale(1.06);
}

.trv-thumb-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--ink-faint);
  font-size: 20px;
}

/* ============ 视频 ============ */
.trv-video-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.trv-video-cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.trv-video-cell video {
  width: 100%;
  max-height: 260px;
  background: #0b1819;
  border: 1px solid var(--hairline);
  border-radius: 8px;
}

.trv-video-name {
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============ 行记正文 ============ */
.trv-note {
  padding: 4px 2px;
}

.trv-note-line {
  margin: 8px 0;
  font-size: 15px;
  color: var(--ink-soft);
  letter-spacing: 1px;
  line-height: 2;
}

.trv-dialog-foot {
  margin: 24px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--ink-faint);
  letter-spacing: 2px;
}

/* ============ 图片最大化查看层（墨青磨砂 + 描述） ============ */
.trv-viewer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 44px 12px 16px;
  box-sizing: border-box;
  /* 底色用半透明墨青（照片虚化层未加载时的兜底），配合背景虚化 */
  background: rgba(10, 24, 27, 0.86);
  -webkit-backdrop-filter: blur(22px) saturate(1.15);
  backdrop-filter: blur(22px) saturate(1.15);
  animation: trv-fade 0.2s ease both;
}

/* 当前照片放大虚化层（外扩一圈避免模糊边缘露白） */
.trv-viewer-bg {
  position: absolute;
  top: -8%;
  right: -8%;
  bottom: -8%;
  left: -8%;
  z-index: 0;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  filter: blur(46px) saturate(1.3);
  transform: scale(1.06);
  opacity: 0.72;
  pointer-events: none;
  animation: trv-bg-fade 0.35s ease both;
}

/* 单独的关键帧：终止值必须与 .trv-viewer-bg 的 opacity 一致，否则动画会覆盖它 */
@keyframes trv-bg-fade {
  from { opacity: 0; }
  to { opacity: 0.72; }
}

/* 墨青压暗层：保证照片与文字的对比度 */
.trv-viewer-scrim {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(130% 100% at 50% 44%, rgba(14, 34, 38, 0.4) 0%, rgba(8, 18, 21, 0.84) 74%);
}

/* 舞台占满除描述行外的全部空间，图片按 contain 等比铺满 */
.trv-viewer-stage {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  overflow: hidden;
}

.trv-viewer-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  /* drop-shadow 只跟随照片本身（透明留白不会产生投影） */
  filter: drop-shadow(0 14px 46px rgba(0, 0, 0, 0.55));
}

.trv-viewer-cap {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  margin: 0;
  max-width: 90vw;
  color: rgba(243, 239, 228, 0.92);
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 1px;
  text-align: center;
  word-break: break-word;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
}

.trv-viewer-count {
  position: absolute;
  z-index: 2;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  letter-spacing: 2px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
}

.trv-viewer-close {
  position: absolute;
  z-index: 2;
  top: 12px;
  right: 14px;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  color: #fff;
  font-size: 22px;
  line-height: 40px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.trv-viewer-close:hover {
  background: rgba(255, 255, 255, 0.22);
}

.trv-viewer-nav {
  position: absolute;
  z-index: 2;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  color: #fff;
  font-size: 20px;
  line-height: 42px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.trv-viewer-nav:hover {
  background: rgba(255, 255, 255, 0.22);
}

.trv-viewer-nav.is-prev { left: 10px; }
.trv-viewer-nav.is-next { right: 10px; }

.trv-cell,
.trv-thumb {
  cursor: zoom-in;
}

/* ============ 响应式 ============ */
@media (max-width: 960px) {
  .trv-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .trv-title {
    font-size: 26px;
    letter-spacing: 6px;
    text-indent: 6px;
  }

  .trv-sub {
    flex-direction: column;
    gap: 10px;
    letter-spacing: 2px;
  }

  .trv-cards {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .trv-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .trv-video-grid {
    grid-template-columns: 1fr;
  }

  .trv-dialog-content {
    padding: 28px 18px 22px;
  }

  /* 详情弹窗：收窄上下留白，缩短地图高度以适配窄屏 */
  .trv-dialog >>> .el-dialog {
    margin: 4vh auto 24px;
    border-radius: 12px;
  }

  .trv-map {
    height: 240px;
  }
}
</style>
